import maActionsButtons from '@/components/button-blue'
import maAppointmentCalendar from '@/modules/Appointment/components/appointmentCalendar'
import maAppointmentHours from '@/modules/Appointment/components/appointmentHours'
import maNavbarStepper from '@/components/navbarStepper'
import flowMixin from '@/modules/Flow/mixins/flowMixin'
import {
  apiPostSinisterScheduleContact,
  apiPostSinisterScheduleReserve,
  apiGetSinisterSchedule
} from '@/modules/Appointment/services/schedule'
import { mapMutations, mapState } from 'vuex'
import { UPDATE_LOADER, FLOW_MODULE_NAME, FNOL_MODULE_NAME, FNOL_IS_WLD_FLOW } from '@/store/types'
import { FLOW_TRANSITIONS } from '@/modules/Flow/constants'
import store from '../../../../store'
import { prepareErrorForSentry } from '@/utils/errorHandling'
import maModal from '@/components/modal'

export default {
  name: 'appointmentContainer',

  mixins: [flowMixin],

  data() {
    return {
      loading: false,
      selectedDate: null,
      selectedHour: {},
      schedule: {
        id: null,
        dates: null
      },
      availableDates: [],
      availableHours: [],
      hoursVisible: false,
      sinisterSchedule: null,
      appointmentModalContent: {
        title: '',
        description: '',
        cta: ''
      },
      pendingNextStep: null
    }
  },

  computed: {
    ...mapState(FLOW_MODULE_NAME, {
      stateFlowViewOptions: 'flowViewOptions'
    }),
    ...mapState(FNOL_MODULE_NAME, {
      stateIsWldFlow: 'isWldFlow',
      stateWldConnection: 'wldConnection',
      stateExecutionCode: 'executionCode'
    }),
    isWldFlow() {
      return this.stateIsWldFlow
    },
    isHoursVisible() {
      return this.hoursVisible
    },
    primaryButtonIsDisabled() {
      return (
        !this.selectedHour.from ||
        this.selectedDate === null ||
        this.availableDates.length === 0
      )
    },
    getBtnType() {
      return this.loading ? 'spinnerBtn' : 'functionalBtnWhite'
    },
    monthsArray() {
      return [
        this.$t('appointment.appointmentContainer.dates.months.january'),
        this.$t('appointment.appointmentContainer.dates.months.february'),
        this.$t('appointment.appointmentContainer.dates.months.march'),
        this.$t('appointment.appointmentContainer.dates.months.april'),
        this.$t('appointment.appointmentContainer.dates.months.may'),
        this.$t('appointment.appointmentContainer.dates.months.june'),
        this.$t('appointment.appointmentContainer.dates.months.july'),
        this.$t('appointment.appointmentContainer.dates.months.august'),
        this.$t('appointment.appointmentContainer.dates.months.september'),
        this.$t('appointment.appointmentContainer.dates.months.october'),
        this.$t('appointment.appointmentContainer.dates.months.november'),
        this.$t('appointment.appointmentContainer.dates.months.december')
      ]
    },

    hasSmartValues() {
      return this.stateFlowViewOptions.hasOwnProperty('smart_values')
    },
    isFrais() {
      if (this.hasSmartValues) {
        return (
          this.stateFlowViewOptions.smart_values.frais.compensation_base !== 0
        )
      } else {
        return false
      }
    },
    isMobilier() {
      if (this.stateFlowViewOptions.smart_values.mobilier) return true
    }
  },

  components: {
    maAppointmentCalendar,
    maAppointmentHours,
    maActionsButtons,
    maNavbarStepper,
    maModal
  },

  methods: {
    ...mapMutations({
      updateLoader: UPDATE_LOADER
    }),
    ...mapMutations(FNOL_MODULE_NAME, {
      mutIsWldFlow: FNOL_IS_WLD_FLOW
    }),

    mapAppointmentDates(sinisterScheduleDates) {
      return sinisterScheduleDates.map((range) => {
        const rangeDateArray = range.date.split('/')
        return new Date(
          rangeDateArray[2],
          rangeDateArray[1] - 1,
          rangeDateArray[0]
        )
      })
    },

    mapAppointmentHours(dateHours) {
      if (!dateHours) return []

      return dateHours.map((hour) => {
        return {
          from: hour.from,
          to: hour.to,
          text:
            hour.from && hour.to
              ? `${hour.from.slice(0, -3)} - ${hour.to.slice(0, -3)}`
              : '-'
        }
      })
    },

    async setSinisterSchedule(successCallback) {
      try {
        const { agendaResultDto: sinisterSchedule } =
          await apiGetSinisterSchedule(this.getSinisterId)

        if (!sinisterSchedule) {
          this.schedule.id = null
          this.schedule.dates = []
          this.availableDates = []
          successCallback()
          return
        }

        this.schedule.id = sinisterSchedule.scheduleId
        this.schedule.dates = sinisterSchedule.ranges
          ? sinisterSchedule.ranges
          : []
        this.availableDates = this.mapAppointmentDates(this.schedule.dates)
        successCallback()
      } catch (error) {
        const context = {
          component: 'appointmentContainer',
          method: 'setSinisterSchedule',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context)

        this.sendSentryError(
          `Error getting appointment schedule: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'setSinisterSchedule'
        )

        this.availableDates = []
        await this.sendAppointmentContact()
      }
    },

    formatSelectedAppointment() {
      return `${this.selectedDate} ${this.selectedHour.from}`
    },

    formatManageDamageAppointmentFlowContactResult() {
      const selectedDateArray = this.selectedDate.split('/')
      const selectedDay = selectedDateArray[0]
        ? parseInt(selectedDateArray[0], 10)
        : '-'
      const selectedMonth = selectedDateArray[1] ? selectedDateArray[1] : '-'
      const selectedMonthLiteral = this.monthsArray[selectedMonth - 1]
      return `${selectedDay}_${selectedMonthLiteral}_${this.selectedHour.from.slice(
        0,
        -3
      )}__-_${this.selectedHour.to.slice(0, -3)}`
    },

    async reserveSinisterAppointment() {
      try {
        await apiPostSinisterScheduleReserve({
          sinisterId: this.getSinisterId,
          scheduleId: this.schedule.id,
          appointmentDate: this.formatSelectedAppointment()
        })
      } catch (error) {
        const context = {
          component: 'appointmentContainer',
          method: 'reserveSinisterAppointment',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context)

        this.sendSentryError(
          `Error reserving sinister appointment: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'reserveSinisterAppointment'
        )
      }
    },

    async sendAppointmentContact() {
      try {
        await apiPostSinisterScheduleContact(this.getSinisterId)
      } catch (error) {
        const context = {
          component: 'appointmentContainer',
          method: 'sendAppointmentContact',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context)

        this.sendSentryError(
          `Error sending sinister schedule contact: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'sendAppointmentContact'
        )
      }
    },

    handleClickDate(selectedDate) {
      if (!this.schedule.dates) return
      this.selectedHour = {}

      this.hoursVisible = false
      this.selectedDate = selectedDate

      setTimeout(() => {
        const selectedDateObject = this.schedule.dates.filter((dates) => {
          return dates.date === selectedDate
        })[0]

        if (!selectedDateObject) return

        this.availableHours = this.mapAppointmentHours(selectedDateObject.hours)
        this.hoursVisible = true
      }, 250)
    },

    handleClickHour(selectedHour) {
      this.selectedHour = this.availableHours.filter((hoursObject) => {
        return hoursObject.from === selectedHour
      })[0]
    },

    async handlePrimaryButtonClick() {
      this.loading = true

      try {
        await this.reserveSinisterAppointment()
        const date = this.formatManageDamageAppointmentFlowContactResult()
        store.dispatch('updateDate', date)

        if (this.isMobilier) {
          this.goToNextState(FLOW_TRANSITIONS.SHOW_MOBILIER)
        } else if (this.isFrais) {
          this.goToNextState(FLOW_TRANSITIONS.SHOW_FRAIS)
        } else {
          this.goToNextState(FLOW_TRANSITIONS.SHOW_RESUME, {
            date: this.formatManageDamageAppointmentFlowContactResult()
          })
        }
      } catch (error) {
        const context = {
          component: 'appointmentContainer',
          method: 'handlePrimaryButtonClick',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context, { willRedirect: true })

        this.sendSentryError(
          `Error reserving appointment: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'handlePrimaryButtonClick'
        )

        const messageNotification = this.$t(
          'appointment.appointmentContainer.notifications.errors.postAppointment'
        )
        this.throwNotification(messageNotification, messageNotification, 'KO')
        this.loading = false
        this.goToNextState(FLOW_TRANSITIONS.FINISHED_ERROR)
      }
    },

    async handlePrimaryButtonClickRDF() {
      this.loading = true

      try {
        await this.reserveSinisterAppointment()
        this.loading = false
        const date = this.formatManageDamageAppointmentFlowContactResult()
        store.dispatch('updateDate', date)

        this.goToNextState(FLOW_TRANSITIONS.FINISHED_OK, {
          date: this.formatManageDamageAppointmentFlowContactResult()
        })
      } catch (error) {
        const context = {
          component: 'appointmentContainer',
          method: 'handlePrimaryButtonClickRDF',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context, { willRedirect: true })

        this.sendSentryError(
          `Error reserving RDF appointment: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'handlePrimaryButtonClickRDF'
        )

        const messageNotification = this.$t(
          'appointment.appointmentContainer.notifications.errors.postAppointment'
        )
        this.throwNotification(messageNotification, messageNotification, 'KO')
        this.loading = false
        this.goToNextState(FLOW_TRANSITIONS.FINISHED_ERROR)
      }
    },

    async handleSecondaryButtonClick() {
      this.loading = true

      try {
        await this.sendAppointmentContact()
      } catch (error) {
        const context = {
          component: 'appointmentContainer',
          method: 'handleSecondaryButtonClick',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context)

        this.sendSentryError(
          `Error sending appointment contact: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'handleSecondaryButtonClick'
        )

        const messageNotification = this.$t(
          'appointment.appointmentContainer.notifications.errors.postContact'
        )
        this.throwNotification(messageNotification, messageNotification, 'KO')
        this.loading = false
      }

      if (this.isMobilier) {
        this.goToNextState(FLOW_TRANSITIONS.SHOW_MOBILIER)
      } else if (this.isFrais) {
        this.goToNextState(FLOW_TRANSITIONS.SHOW_FRAIS)
      } else {
        this.goToNextState(FLOW_TRANSITIONS.SHOW_RESUME)
      }
    },

    async handleSecondaryButtonClickRDF() {
      this.loading = true

      try {
        await this.sendAppointmentContact()
      } catch (error) {
        const context = {
          component: 'appointmentContainer',
          method: 'handleSecondaryButtonClickRDF',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context, { willRedirect: true })

        this.sendSentryError(
          `Error sending RDF appointment contact: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'handleSecondaryButtonClickRDF'
        )

        const messageNotification = this.$t(
          'appointment.appointmentContainer.notifications.errors.postContact'
        )
        this.throwNotification(messageNotification, messageNotification, 'KO')
        this.loading = false
        this.goToNextState(FLOW_TRANSITIONS.FINISHED_ERROR)
      }

      this.goToNextState(FLOW_TRANSITIONS.FINISHED_ERROR)
    },

    // WLD Handlers - systema_wld flow
    async handlePrimaryButtonClickWLD() {
      this.loading = true

      try {
        await this.reserveSinisterAppointment()
        const date = this.formatManageDamageAppointmentFlowContactResult()
        store.dispatch('updateDate', date)

        this.mutIsWldFlow({
          isWld: true,
          connection: this.stateWldConnection,
          withAppointment: true
        })

        this.pendingNextStep = {
          transition: FLOW_TRANSITIONS.FINISHED_OK_RDF,
          payload: { date }
        }

        this.openSuccessModal({
          title: this.$t('appointment.appointmentContainer.wld.confirmationModalRDV.title'),
          description: this.$t('appointment.appointmentContainer.wld.confirmationModalRDV.body'),
          cta: this.$t('appointment.appointmentContainer.wld.confirmationModalRDV.cta')
        })
      } catch (error) {
        const context = {
          component: 'appointmentContainer',
          method: 'handlePrimaryButtonClickWLD',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context, { willRedirect: true })

        this.sendSentryError(
          `Error reserving WLD appointment: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'handlePrimaryButtonClickWLD'
        )

        const messageNotification = this.$t(
          'appointment.appointmentContainer.notifications.errors.postAppointment'
        )
        this.throwNotification(messageNotification, messageNotification, 'KO')
        this.loading = false
        this.goToNextState(FLOW_TRANSITIONS.FINISHED_ERROR)
      }
    },

    async handleSecondaryButtonClickWLD() {
      this.loading = true

      try {
        await this.sendAppointmentContact()
      } catch (error) {
        const context = {
          component: 'appointmentContainer',
          method: 'handleSecondaryButtonClickWLD',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context, { willRedirect: true })

        this.sendSentryError(
          `Error sending WLD appointment contact: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'handleSecondaryButtonClickWLD'
        )

        const messageNotification = this.$t(
          'appointment.appointmentContainer.notifications.errors.postContact'
        )
        this.throwNotification(messageNotification, messageNotification, 'KO')
        // No return here - continue to fnol-flow even if contact fails
      }

      this.mutIsWldFlow({
        isWld: true,
        connection: this.stateWldConnection,
        withAppointment: false
      })

      this.pendingNextStep = {
        type: 'VIEW',
        target: 'fnol-flow'
      }

      this.openSuccessModal({
        title: this.$t('appointment.appointmentContainer.wld.confirmationModalRap.title'),
        description: this.$t('appointment.appointmentContainer.wld.confirmationModalRap.body'),
        cta: this.$t('appointment.appointmentContainer.wld.confirmationModalRap.cta')
      })
    },

    openSuccessModal({ title, description, cta }) {
      this.appointmentModalContent = { title, description, cta }
      if (this.$refs.modalAppointmentConfirmation) {
        this.$refs.modalAppointmentConfirmation.showModal = true
      }
    },

    async handleConfirmModalNextStep() {
      if (this.$refs.modalAppointmentConfirmation) {
        this.$refs.modalAppointmentConfirmation.showModal = false
      }

      if (this.pendingNextStep) {
        if (this.pendingNextStep.type === 'VIEW') {
          this.goToView(this.pendingNextStep.target)
        } else {
          const { transition, payload } = this.pendingNextStep
          await this.goToNextState(transition, payload)
        }
      }
    },

    handleTertiaryButtonClick() {
      this.goToNextState(FLOW_TRANSITIONS.SHOW_COMPENSATION_TYPE)
      this.goToNextState(FLOW_TRANSITIONS.FINISHED_OK)
    },

    hasAvailableDates() {
      return this.availableDates && this.availableDates.length > 0
    }
  },

  beforeCreate() {
    if (this.availableDates && this.availableDates.length === 0) {
      this.goToNextState(FLOW_TRANSITIONS.FINISHED_KO)
    }
  },

  async mounted() {
    if (!['COMPENSATION_MOBILIER_REN', 'COMPENSATION_FRAIS_REN'].includes(this.getFlowState)) {
      this.updateLoader(true)
      await this.setSinisterSchedule(() => {
        this.updateLoader(false)
      })
    }
  }
}
